import { Box, IconButton, Stack, TextField, useTheme } from "@mui/material";
import * as React from "react";

import { TemplateField } from "@/shared/components/TemplateField";
import { type TriggerTemplateCatalog } from "@/shared/domain/triggers";
import {
  AddCircleOutlineIcon,
  RemoveCircleOutlineIcon,
} from "@/shared/foundation/icons/mui";

interface ActionInputDataProps {
  inputData: string[];
  setInputData: (arg: string[]) => void;
  minRequired: number;
  /**
   * When set, patterns accept `{{path}}` placeholders with autocomplete over
   * this catalog (a trigger target's file patterns are templates).
   */
  templateCatalog?: TriggerTemplateCatalog;
}

export const ActionInputData: React.FC<ActionInputDataProps> = ({
  inputData: givenInputData,
  setInputData,
  minRequired,
  templateCatalog,
}) => {
  const theme = useTheme();
  // Draw an empty list as one blank field, so there is a field to type in and
  // a "+" button to add more.
  const inputData = givenInputData.length > 0 ? givenInputData : [""];

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const newInputData = [...inputData];
    newInputData[index] = event.target.value;
    setInputData(newInputData);
  };

  return (
    <>
      <Stack
        sx={{
          maxWidth: "60ch",
        }}
        spacing={2}
      >
        {inputData &&
          inputData.map((_input, index: number) => (
            <Box
              key={"input-data-" + index}
              sx={{ display: "flex", alignItems: "center" }}
            >
              {templateCatalog !== undefined ? (
                <Box sx={{ width: "100%" }}>
                  <TemplateField
                    ariaLabel={`File pattern ${index + 1}`}
                    placeholder="Enter a file pattern, include all files with **/*"
                    value={inputData[index]}
                    onChange={(next) => {
                      const newInputData = [...inputData];
                      newInputData[index] = next;
                      setInputData(newInputData);
                    }}
                    catalog={templateCatalog}
                    dataCy={`input-pattern-${index}`}
                  />
                </Box>
              ) : (
                <TextField
                  id="outlined-action-input-data"
                  placeholder="Enter a file pattern, include all files with **/*"
                  size="small"
                  value={inputData[index]}
                  sx={{ width: 400 }}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleInputChange(event, index);
                  }}
                />
              )}
              {index === inputData.length - 1 && (
                <IconButton
                  aria-label="add-input-data"
                  size="small"
                  sx={{ ml: theme.spacing(0.5) }}
                  onClick={() => setInputData([...inputData, ""])}
                >
                  <AddCircleOutlineIcon color="primary" fontSize="small" />
                </IconButton>
              )}
              {inputData.length > minRequired && (
                <IconButton
                  aria-label="remove-input-data"
                  size="small"
                  sx={{ ml: theme.spacing(0.5) }}
                  onClick={() => {
                    const splicedData = inputData;
                    splicedData.splice(index, 1);
                    setInputData([...splicedData]);
                  }}
                >
                  <RemoveCircleOutlineIcon color="primary" fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
      </Stack>
    </>
  );
};
